import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEmails, useDeleteEmail } from '../../hooks/useEmails';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, ConfirmModal, Input, Table } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type { Email } from '../../types';

export default function EmailList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useEmails();
  const deleteEmail = useDeleteEmail();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    email: Email | null;
  }>({ isOpen: false, email: null });

  const emails = data?.items || [];

  const filteredEmails = emails.filter((email) =>
    email.emailAddress.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deleteModal.email) return;

    try {
      await deleteEmail.mutateAsync(deleteModal.email.emailId);
      success('Email address deleted successfully');
      setDeleteModal({ isOpen: false, email: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete email address');
    }
  };

  const columns = [
    {
      key: 'emailAddress',
      header: 'Email Address',
      render: (email: Email) => (
        <p className="font-medium text-foreground font-mono">{email.emailAddress}</p>
      ),
    },
    {
      key: 'allowedSenders',
      header: 'Allowed Senders',
      render: (email: Email) => (
        <Badge variant={email.allowedSenders.length > 0 ? 'info' : 'gray'}>
          {email.allowedSenders.length > 0
            ? `${email.allowedSenders.length} sender${email.allowedSenders.length !== 1 ? 's' : ''}`
            : 'All senders'}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (email: Email) => (
        <span className="text-muted-foreground">
          {new Date(email.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (email: Email) => (
        <div className="flex items-center gap-2">
          <Link to={routes.console.spaceConnectionEmailEdit(spaceId, email.emailId)}>
            <Button variant="ghost" size="sm">
              Edit
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, email });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Email Addresses</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage email inboxes for knowledge base ingestion
          </p>
        </div>
        <Link to={routes.console.spaceConnectionEmailNew(spaceId)}>
          <Button>Create Email</Button>
        </Link>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search email addresses..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Emails table */}
      <Table
        columns={columns}
        data={filteredEmails}
        keyExtractor={(email) => email.emailId}
        onRowClick={(email) => navigate(routes.console.spaceConnectionEmail(spaceId, email.emailId))}
        emptyMessage="No email addresses found. Create your first email to get started."
        isLoading={isLoading}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, email: null })}
        onConfirm={handleDelete}
        title="Delete Email Address"
        message={`Are you sure you want to delete "${deleteModal.email?.emailAddress}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteEmail.isPending}
      />
    </div>
  );
}
